import React, { useState, useEffect, useCallback } from "react";
import { Form } from "react-bootstrap";
import { useDropzone } from "react-dropzone"; // DODANO
import { getImageUrl } from "../../utils/imageUtils";
import "bootstrap/dist/css/bootstrap.min.css";
import "./ImageUpload.css";

const MAX_FILE_SIZE_MB = 8;
const MAX_FILE_SIZE_BYTES = MAX_FILE_SIZE_MB * 1024 * 1024;
const MAX_IMAGE_COUNT = 7;
const ALLOWED_MIME_TYPES = ["image/jpeg", "image/png"];
const ALLOWED_EXTENSIONS_DISPLAY = ".png, .jpeg, .jpg";

export default function ImageUpload({ images, setImages, initialFile, setIsUploading }) {
  const [processingFiles, setProcessingFiles] = useState([]);
  const [globalError, setGlobalError] = useState(null);
  
  // Opomba: fileInputRef ni več potreben pri dropzone, a ga pustimo, če bi ga kje drugje rabil
  // const fileInputRef = useRef(null); 

  useEffect(() => {
    return () => {
      processingFiles.forEach(file => {
        if (file.localPreview) {
          URL.revokeObjectURL(file.localPreview);
        }
      });
    };
  }, [processingFiles]);

  const processAndUploadFiles = async (filesToProcess) => {
    if (filesToProcess.length === 0) return;

    // 1. Obvesti starša, da se je proces začel.
    setIsUploading(true);

    try {
      setGlobalError(null);

      const validFilesToProcess = [];
      const initialProcessingEntries = [];

      let slotsAvailable = MAX_IMAGE_COUNT - images.length - processingFiles.filter(p => !p.error).length;

      for (const file of filesToProcess) {
        if (slotsAvailable <= 0) {
          setGlobalError(`Dosegli ste največje število slik (${MAX_IMAGE_COUNT}).`);
          break;
        }

        const tempId = `${file.name}-${Date.now()}-${Math.random()}`;
        let error = null;

        // Tvoja originalna logika preverjanja napak ostaja nespremenjena
        if (!ALLOWED_MIME_TYPES.includes(file.type)) {
          error = `Napačen format. Dovoljeno: ${ALLOWED_EXTENSIONS_DISPLAY}.`;
        } else if (file.size > MAX_FILE_SIZE_BYTES) {
          error = `Datoteka presega ${MAX_FILE_SIZE_MB}MB.`;
        }

        const localPreviewUrl = !error ? URL.createObjectURL(file) : null;

        initialProcessingEntries.push({
          id: tempId,
          name: file.name,
          isLoading: !error,
          error: error,
          localPreview: localPreviewUrl,
        });

        if (!error) {
          validFilesToProcess.push({ file, tempId });
          slotsAvailable--;
        }
      }

      setProcessingFiles(prev => [...prev, ...initialProcessingEntries]);
      
      // Dropzone ne potrebuje reseta value-a kot input, zato smo izpustili ref.value = ""

      if (validFilesToProcess.length === 0) {
        return; 
      }

      const uploadPromises = validFilesToProcess.map(async ({ file, tempId }) => {
        try {
          const query = new URLSearchParams({ filename: file.name, contentType: file.type });
          const presignResp = await fetch(`${process.env.REACT_APP_API_URL}/upload-url?${query}`);
          if (!presignResp.ok) {
            const errData = await presignResp.json().catch(() => ({}));
            throw new Error(errData.error || "Napaka pri pridobivanju URL-ja.");
          }

          const { uploadUrl, key } = await presignResp.json();
          if (!uploadUrl || !key) throw new Error("Neveljaven odgovor strežnika.");

          const r2Resp = await fetch(uploadUrl, { method: "PUT", headers: { "Content-Type": file.type }, body: file });
          if (!r2Resp.ok) throw new Error("Nalaganje ni uspelo.");

          return { tempId, key, fileName: file.name };
        } catch (err) {
          console.error("Napaka pri nalaganju:", file.name, err);
          return Promise.reject({ tempId, error: err.message });
        }
      });

      const results = await Promise.allSettled(uploadPromises);

      let newlyUploadedImages = [];
      results.forEach(result => {
        if (result.status === 'fulfilled') {
          const { tempId, key, fileName } = result.value;
          // Ohranjamo tvojo logiko z is_newly_uploaded
          newlyUploadedImages.push({ id: key, image_key: key, is_primary: false, file_name: fileName, is_newly_uploaded: true });
          setProcessingFiles(prev => prev.filter(pf => pf.id !== tempId));
        } else {
          const { tempId, error } = result.reason;
          setProcessingFiles(prev =>
            prev.map(pf => (pf.id === tempId ? { ...pf, isLoading: false, error: error || "Neznana napaka." } : pf))
          );
        }
      });

      if (newlyUploadedImages.length > 0) {
        setImages(prevImages => {
          let updatedImages = [...prevImages, ...newlyUploadedImages];
          if (!updatedImages.some(img => img.is_primary) && updatedImages.length > 0) {
            updatedImages[0].is_primary = true;
          }
          return updatedImages.slice(0, MAX_IMAGE_COUNT);
        });
      }

    } finally {
      setIsUploading(false);
    }
  };

  // --- DROPZONE IMPLEMENTACIJA ---
  const canUploadMore = (images.length + processingFiles.filter(p => !p.error).length) < MAX_IMAGE_COUNT;
  const isUploadDisabled = !canUploadMore || processingFiles.some(pf => pf.isLoading);

  const onDrop = useCallback((acceptedFiles, fileRejections) => {
    // Združimo vse datoteke (sprejete in zavrnjene s strani dropzone), 
    // da se tvoja funkcija processAndUploadFiles odloči o napakah (velikost, tip itd.)
    // in prikaže tvoja specifična sporočila.
    const allFiles = [...acceptedFiles, ...fileRejections.map(fr => fr.file)];
    processAndUploadFiles(allFiles);
  }, [images, processingFiles]); // Dependency array prilagojen

  const { getRootProps, getInputProps, isDragActive, open } = useDropzone({
    onDrop,
    disabled: isUploadDisabled,
    accept: {
        'image/jpeg': [],
        'image/png': []
    },
    noClick: false,
    noKeyboard: false,
    multiple: true
  });

  useEffect(() => {
    if (initialFile) {
      const isAlreadyProcessed =
        images.some(img => img.file_name === initialFile.name) ||
        processingFiles.some(pf => pf.name === initialFile.name);

      if (!isAlreadyProcessed) {
        processAndUploadFiles([initialFile]);
      }
    }
  }, [initialFile]);

  const removeImage = (idToRemove) => {
    setImages((prevImages) => {
      const imageToRemove = prevImages.find(img => img.id === idToRemove);
      let nextImages = prevImages.filter((img) => img.id !== idToRemove);
      if (imageToRemove && imageToRemove.is_primary && nextImages.length > 0) {
        if (!nextImages.some(img => img.is_primary)) {
          nextImages[0].is_primary = true;
        }
      }
      return nextImages;
    });
  };

  const removeProcessingFile = (idToRemove) => {
    setProcessingFiles(prev => prev.filter(pf => pf.id !== idToRemove));
  };

  const makePrimary = (idToMakePrimary) => {
    setImages((prevImages) =>
      prevImages.map((img) => ({ ...img, is_primary: img.id === idToMakePrimary }))
    );
  };

  const allDisplayItems = [
    ...images.map(img => ({ ...img, type: 'uploaded' })),
    ...processingFiles.map(pf => ({ ...pf, type: 'processing' }))
  ].sort((a, b) => (a.id < b.id) ? -1 : 1);

  return (
    <div className="image-upload-container container py-3">
      {/* --- DROPZONE UI --- */}
      <Form.Group className="mb-3">
        <div 
            {...getRootProps()} 
            className={`dropzone-area ${isDragActive ? 'active' : ''} ${isUploadDisabled ? 'disabled' : ''}`}
        >
            <input {...getInputProps()} />
            <div className="text-center">
                {isDragActive ? (
                    <p className="mb-0 text-primary fw-bold">Spustite datoteke tukaj ...</p>
                ) : (
                    <>
                        <p className="mb-1 fw-bold text-dark">Povlecite slike sem ali kliknite za nalaganje</p>
                        <p className="mb-0 text-muted small">
                            (Dovoljeno: {ALLOWED_EXTENSIONS_DISPLAY}, Max: {MAX_FILE_SIZE_MB}MB)
                        </p>
                    </>
                )}
            </div>
        </div>
        
        <div className="d-flex justify-content-between align-items-center mt-2">
            <Form.Text className="text-muted">
             {/* Opcijski dodatni tekst ali prazen prostor */}
            </Form.Text>
            <Form.Text className="text-muted">
                {images.length + processingFiles.filter(p => !p.error).length} / {MAX_IMAGE_COUNT} slik
            </Form.Text>
        </div>

        {!canUploadMore && (
          <Form.Text className="text-danger d-block mt-1">
            Dosegli ste največje število slik.
          </Form.Text>
        )}
      </Form.Group>

      {globalError && <div className="alert alert-danger mt-2">{globalError}</div>}

      {(allDisplayItems.length > 0) && (
        <div className="row g-3 image-previews-row">
          {allDisplayItems.map((item) => (
            <div key={item.id} className="col-6 col-sm-4 col-md-3 col-lg-2 position-relative mb-3">
              <div
                className={`image-preview-item ${item.type === 'uploaded' && item.is_primary ? "is-primary" : ""} ${item.type === 'processing' && item.error ? "has-error" : ""}`}
              >
                {item.type === 'uploaded' ? (
                  <div className="image-preview-wrapper">
                    <img
                      src={getImageUrl(item.image_key, item.is_newly_uploaded ? 'original' : 'card')}
                      alt={item.file_name || 'Uploaded image'}
                      className="image-preview-content"
                      onClick={() => makePrimary(item.id)}
                      loading="lazy"
                    />
                  </div>
                ) : (
                  <div className="img-fluid rounded placeholder-preview d-flex flex-column align-items-center justify-content-center">
                    {item.localPreview ? (
                      <img src={item.localPreview} alt="Lokalni predogled" className="image-preview-content" />
                    ) : (
                      <div className="placeholder-icon">🖼️</div>
                    )}
                    {item.isLoading && (
                      <div className="spinner-overlay">
                        <div className="spinner-border spinner-border-sm text-primary" role="status"></div>
                      </div>
                    )}
                    <small className="text-muted text-break-all px-1 text-center elipsis-text">{item.name}</small>
                    {item.error && <small className="text-danger error-text mt-1">{item.error}</small>}
                  </div>
                )}

                {item.type === 'uploaded' && (
                  <>
                    <button type="button" className="btn btn-sm btn-danger btn-remove-img" onClick={() => removeImage(item.id)}>×</button>
                    {!item.is_primary && <button type="button" className="btn btn-sm btn-success btn-make-primary" onClick={() => makePrimary(item.id)}>★</button>}
                    {item.is_primary && <span className="badge bg-success primary-badge">Primarna</span>}
                  </>
                )}
                {item.type === 'processing' && !item.isLoading && item.error && (
                  <button type="button" className="btn btn-sm btn-warning btn-remove-img" onClick={() => removeProcessingFile(item.id)}>×</button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}